import { useEffect } from 'react';
import type { z } from 'zod';
import { useLatest } from 'ahooks';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useJwt } from '~/hooks/use-jwt';
import { useAppData } from '~/modules/root';

/**
 * Hook for connecting to the ArchiPro WebSocket server. Handles JWT
 * authentication, heartbeat, exponential-backoff reconnection, and Zod-based
 * message validation/filtering.
 */
export function useWebSocketServer<T extends z.ZodTypeAny>({
    schema,
    onMessage,
}: {
    /**
     * `Zod` schema to validate and filter incoming WebSocket messages.
     */
    schema: T;
    /**
     * Callback invoked with each validated message.
     */
    onMessage?: (message: z.infer<T>) => void;
}) {
    const { jwt: token } = useJwt(['readUser', 'readChat'], 'WebSocketServer');
    const webSocketUrl = useAppData().webSocketUrl;

    const { lastJsonMessage, readyState, sendMessage, sendJsonMessage } =
        useWebSocket<z.infer<T>>(
            webSocketUrl,
            {
                // TODO: Remove queryParams when migrated to new auth system.
                queryParams: token ? { token } : undefined,
                protocols: token ? ['Authorization', token] : undefined,
                share: true,
                heartbeat: {
                    timeout: 10_000,
                    interval: 30_000,
                },
                filter: (message) => {
                    if (isHeartbeatMessage(message.data)) {
                        return false;
                    }
                    if (typeof message.data !== 'string') {
                        return false;
                    }
                    try {
                        schema.parse(JSON.parse(message.data));
                        return true;
                    } catch {
                        return false;
                    }
                },
                shouldReconnect: () => !!token,
                reconnectAttempts: 20,
                reconnectInterval: (lastAttemptNumber) => {
                    if (lastAttemptNumber === 0) {
                        return 100; // Try to reconnect immediately on first time.
                    } else if (lastAttemptNumber <= 2) {
                        return 2_000; // 2 seconds
                    }
                    // Exponential backoff after the 3rd attempt.
                    const baseDelay = 30_000; // 30 seconds
                    const maxDelay = 3_600_000; // 1 hour
                    const exponent = lastAttemptNumber - 3;
                    return Math.min(
                        baseDelay * Math.pow(2, exponent),
                        maxDelay
                    );
                },
            },
            !!token
        );

    const latestOnMessage = useLatest(onMessage);

    useEffect(() => {
        if (lastJsonMessage && latestOnMessage.current) {
            latestOnMessage.current(lastJsonMessage);
        }
    }, [lastJsonMessage, latestOnMessage]);

    return {
        /**
         * The last validated message received from the server.
         */
        lastMessage: lastJsonMessage,
        /**
         * The WebSocket connection is currently open.
         */
        isConnected: readyState === ReadyState.OPEN,
        /**
         * The ready-state of the WebSocket connection.
         */
        readyState,
        /**
         * Send a message to the server.
         */
        sendMessage,
        /**
         * Send a JSON message to the server.
         */
        sendJsonMessage,
    };
}

/**
 * Check if a message from the WebSocket is a heartbeat message.
 * @param message The message to check.
 * @returns Whether the message is a heartbeat message.
 */
function isHeartbeatMessage(message: unknown) {
    return !message || message === 'ping' || message === 'pong';
}
